'use client';

import { IBlogConfig } from '@/api/types/blog';
import Image from '@/components/Image';
import MagicJump, { JumpTarget } from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { Navigation } from 'swiper/modules';
import { Swiper as SwiperComponent, SwiperProps, SwiperSlide } from 'swiper/react';
import SwiperNavigationButtons from './SwiperNavigationButtons';
import { useTranslations } from '@/i18n/use-translations';

// 菜单项类型
interface TopMenuItem {
  id: string | number;
  title: string;
  pc_image?: string;
  wap_image?: string;
  jump_target: JumpTarget;
}

// 顶部菜单数据类型
interface TopMenuData extends IBlogConfig {
  top_menus?: TopMenuItem[];
}

// 神策参数类型
interface SensorParams {
  mkt_name: string;
  pits_index: number;
  pits_jump_type: string;
  pits_jump_content: string;
}

interface TopMenusProps {
  topMenuData?: TopMenuData;
  sdBlogPageMktExp?: (params: SensorParams) => void;
  sdBlogPageMktClick?: (params: SensorParams) => void;
  ts?: number;
  className?: string;
}

const TopMenus: React.FC<TopMenusProps> = ({
  topMenuData = {},
  sdBlogPageMktExp = () => {},
  sdBlogPageMktClick = () => {},
  className = '',
}) => {
  const t = useTranslations('blog.components');
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;
  const swiperRef = useRef<any>(null);

  const swiperOptions = useMemo(() => {
    const options: SwiperProps = {
      modules: [Navigation],
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      slidesPerView: 'auto' as const,
      navigation: {
        prevEl: '.top-menus-prev-btn',
        nextEl: '.top-menus-next-btn',
      },
    };
    if (deviceType === EPlatform.WEB) {
      options.spaceBetween = 24;
      options.slidesOffsetAfter = 48;
    } else {
      options.spaceBetween = 12;
      options.slidesOffsetBefore = 14;
      options.slidesOffsetAfter = 14;
    }
    return options;
  }, [deviceType]);

  // 获取菜单列表
  const articles = React.useMemo(() => {
    try {
      const topMenus = topMenuData?.top_menus || [];
      return Array.isArray(topMenus) ? topMenus : [];
    } catch (error) {
      logger.error('TopMenus error', error);
      return [];
    }
  }, [topMenuData]);

  // 生成神策参数
  const getSensorParams = useCallback((item: TopMenuItem, index: number): SensorParams => {
    return {
      mkt_name: 'Top Menus',
      pits_index: index,
      pits_jump_type: `${item.jump_target.jump_type}`,
      pits_jump_content: `${item.jump_target.Jump_target}`,
    };
  }, []);

  // 处理菜单点击
  const handleMenuClick = useCallback(
    (item: TopMenuItem, index: number) => {
      sdBlogPageMktClick(getSensorParams(item, index));
    },
    [sdBlogPageMktClick, getSensorParams],
  );

  // 菜单项组件
  const TopMenuItem: React.FC<{
    item: TopMenuItem;
    index: number;
  }> = ({ item, index }) => {
    const { ref, inView } = useInView({
      threshold: 0.5,
      triggerOnce: true,
    });

    // 处理曝光
    React.useEffect(() => {
      if (inView) {
        sdBlogPageMktExp(getSensorParams(item, index));
      }
    }, [inView, item, index]);

    return (
      <div ref={ref} className="top-menu-item">
        <MagicJump
          prefetch={false}
          item={item.jump_target as any}
          className="block"
          aria-label={item.title}
          onBeforeJump={() => handleMenuClick(item, index)}
          openInNewTab
        >
          <div className="img-box aspect-video overflow-hidden">
            <Image
              src={isWeb ? item.pc_image : item.wap_image}
              alt={item.title}
              width={307}
              height={173}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className={`title font-bb mt-24 truncate ${isWeb ? 'text-20' : 'text-16'}`}>
            {item.title}
          </div>
        </MagicJump>
      </div>
    );
  };

  // 如果没有菜单数据，不渲染
  if (!articles.length) {
    return null;
  }

  return (
    <div
      className={`
      top-read-articles box-border
      ${className}
      ${deviceType === EPlatform.WEB ? 'w-[calc(100%+48px)] mt-[2.5vw]' : 'w-full mt-32'}
    `}
    >
      {/* 标题 */}
      <div
        className={`title ${deviceType === EPlatform.WEB ? 'flex justify-between items-baseline' : 'pl-14'}`}
      >
        <h4 className={`font-bb ${deviceType === EPlatform.WEB ? 'text-32' : 'text-20'}`}>
          {t('topMenus')}
        </h4>
        <SwiperNavigationButtons
          prevButtonClass="top-menus-prev-btn"
          nextButtonClass="top-menus-next-btn"
          prevButtonExtraClass="blog-top-read-articles-nav-left"
          nextButtonExtraClass="blog-top-read-articles-nav-right"
        />
      </div>

      {/* 菜单轮播 */}
      <div className={`articles ${deviceType === EPlatform.WEB ? 'mt-[32px]' : 'mt-20'}`}>
        <SwiperComponent
          {...swiperOptions}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          className="top-read-swiper"
        >
          {articles.map((item, index) => (
            <SwiperSlide
              key={item.id}
              className="swiper-item"
              style={{ width: `${deviceType === EPlatform.WEB ? '356px' : '307px'}` }}
            >
              <TopMenuItem item={item} index={index} />
            </SwiperSlide>
          ))}
        </SwiperComponent>
      </div>
    </div>
  );
};

export default TopMenus;
